import { useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import firebase from "../../firebase";

const MINIMUM_PASSWORD_LENGTH = 6;

function getActionCodeErrorMessage(error) {
    switch (error && error.code) {
        case "auth/expired-action-code":
            return "This password link has expired. Please request a new one.";
        case "auth/invalid-action-code":
            return "This password link is invalid or has already been used. Please request a new one.";
        case "auth/user-disabled":
            return "This account has been disabled. Please contact your administrator.";
        case "auth/user-not-found":
            return "We could not find the account for this password link.";
        case "auth/weak-password":
            return (
                error.message ||
                "That password does not meet the security requirements."
            );
        default:
            return "We could not process this password link. Please request a new one and try again.";
    }
}

export default function SetPasswordPage() {
    const location = useLocation();

    const [status, setStatus] = useState("checking");
    const [accountEmail, setAccountEmail] = useState("");
    const [actionCode, setActionCode] = useState("");
    const [password, setPassword] = useState("");
    const [confirmedPassword, setConfirmedPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmedPassword, setShowConfirmedPassword] = useState(false);
    const [validationMessage, setValidationMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const mode = params.get("mode");
        const oobCode = params.get("oobCode");

        setStatus("checking");
        setErrorMessage("");

        if (mode !== "resetPassword" || !oobCode) {
            setErrorMessage(
                "This password link is incomplete or invalid. Please request a new one.",
            );
            setStatus("error");
            return;
        }

        firebase
            .auth()
            .verifyPasswordResetCode(oobCode)
            .then((email) => {
                setActionCode(oobCode);
                setAccountEmail(email);
                setStatus("ready");
            })
            .catch((error) => {
                setErrorMessage(getActionCodeErrorMessage(error));
                setStatus("error");
            });
    }, [location.search]);

    function validatePasswords() {
        if (password.length < MINIMUM_PASSWORD_LENGTH) {
            return `Your password must be at least ${MINIMUM_PASSWORD_LENGTH} characters long.`;
        }

        if (password !== confirmedPassword) {
            return "The passwords do not match.";
        }

        return "";
    }

    function handleSubmit(event) {
        event.preventDefault();

        const message = validatePasswords();
        setValidationMessage(message);

        if (message) {
            return;
        }

        setStatus("submitting");
        setErrorMessage("");

        firebase
            .auth()
            .confirmPasswordReset(actionCode, password)
            .then(() => {
                setPassword("");
                setConfirmedPassword("");
                setStatus("success");
            })
            .catch((error) => {
                setErrorMessage(getActionCodeErrorMessage(error));
                setStatus(
                    error && error.code === "auth/weak-password"
                        ? "ready"
                        : "error",
                );
            });
    }

    const isSubmitting = status === "submitting";

    return (
        <>
            <Helmet>
                <title>Choose your Password | Nodiant</title>
                <meta
                    name="description"
                    content="Choose your Nodiant Online password"
                />
            </Helmet>

            <Box
                component="main"
                sx={{
                    minHeight: "100vh",
                    bgcolor: "background.default",
                    px: 2,
                    py: { xs: 3, sm: 6 },
                    display: "flex",
                    alignItems: { xs: "flex-start", sm: "center" },
                    justifyContent: "center",
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        boxSizing: "border-box",
                        width: "100%",
                        maxWidth: 520,
                        borderRadius: 2,
                        p: { xs: 3, sm: 4 },
                        textAlign: "center",
                    }}
                >
                    <Box
                        component="img"
                        src="/images/logo180.png"
                        alt="Nodiant"
                        sx={{
                            display: "block",
                            width: 72,
                            height: 72,
                            mx: "auto",
                            mb: 3,
                            borderRadius: 1,
                        }}
                    />

                    <Typography
                        component="h1"
                        sx={{
                            color: "text.primary",
                            fontFamily: "Helvetica, Arial, sans-serif",
                            fontSize: 22,
                            fontWeight: 700,
                            mb: 2,
                        }}
                    >
                        Choose your Password
                    </Typography>

                    {status === "checking" && (
                        <Box
                            role="status"
                            sx={{
                                py: 4,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 2,
                            }}
                        >
                            <CircularProgress size={32} />
                            <Typography sx={{ color: "text.secondary" }}>
                                Checking your password link…
                            </Typography>
                        </Box>
                    )}

                    {(status === "ready" || status === "submitting") && (
                        <Box
                            component="form"
                            noValidate
                            onSubmit={handleSubmit}
                        >
                            <Typography
                                sx={{
                                    color: "text.secondary",
                                    fontFamily:
                                        "Helvetica, Arial, sans-serif",
                                    fontSize: 15,
                                    mb: 1,
                                }}
                            >
                                Create a password for
                            </Typography>
                            <Typography
                                sx={{
                                    color: "text.primary",
                                    fontFamily:
                                        "Helvetica, Arial, sans-serif",
                                    fontSize: 15,
                                    fontWeight: 700,
                                    overflowWrap: "anywhere",
                                    mb: 3,
                                }}
                            >
                                {accountEmail}
                            </Typography>

                            {errorMessage && (
                                <Alert
                                    severity="error"
                                    sx={{ mb: 2, textAlign: "left" }}
                                >
                                    {errorMessage}
                                </Alert>
                            )}

                            <TextField
                                value={password}
                                onChange={(event) => {
                                    setPassword(event.target.value);
                                    setValidationMessage("");
                                }}
                                label="New password"
                                type={showPassword ? "text" : "password"}
                                autoComplete="new-password"
                                autoFocus
                                required
                                fullWidth
                                disabled={isSubmitting}
                                error={Boolean(validationMessage)}
                                inputProps={{
                                    minLength: MINIMUM_PASSWORD_LENGTH,
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label={
                                                    showPassword
                                                        ? "Hide password"
                                                        : "Show password"
                                                }
                                                onClick={() =>
                                                    setShowPassword(
                                                        (current) => !current,
                                                    )
                                                }
                                                edge="end"
                                                disabled={isSubmitting}
                                            >
                                                {showPassword ? (
                                                    <VisibilityOff />
                                                ) : (
                                                    <Visibility />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ mb: 2 }}
                            />

                            <TextField
                                value={confirmedPassword}
                                onChange={(event) => {
                                    setConfirmedPassword(event.target.value);
                                    setValidationMessage("");
                                }}
                                label="Confirm new password"
                                type={
                                    showConfirmedPassword ? "text" : "password"
                                }
                                autoComplete="new-password"
                                required
                                fullWidth
                                disabled={isSubmitting}
                                error={Boolean(validationMessage)}
                                helperText={
                                    validationMessage ||
                                    `Use at least ${MINIMUM_PASSWORD_LENGTH} characters.`
                                }
                                inputProps={{
                                    minLength: MINIMUM_PASSWORD_LENGTH,
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label={
                                                    showConfirmedPassword
                                                        ? "Hide confirmed password"
                                                        : "Show confirmed password"
                                                }
                                                onClick={() =>
                                                    setShowConfirmedPassword(
                                                        (current) => !current,
                                                    )
                                                }
                                                edge="end"
                                                disabled={isSubmitting}
                                            >
                                                {showConfirmedPassword ? (
                                                    <VisibilityOff />
                                                ) : (
                                                    <Visibility />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                disabled={isSubmitting}
                                sx={{
                                    mt: 3,
                                    minHeight: 46,
                                    fontFamily:
                                        "Helvetica, Arial, sans-serif",
                                    fontSize: 15,
                                    textTransform: "none",
                                }}
                            >
                                {isSubmitting ? (
                                    <>
                                        <CircularProgress
                                            size={20}
                                            color="inherit"
                                            sx={{ mr: 1 }}
                                        />
                                        Saving password…
                                    </>
                                ) : (
                                    "Choose your password"
                                )}
                            </Button>
                        </Box>
                    )}

                    {status === "success" && (
                        <Box role="status">
                            <Alert severity="success" sx={{ mb: 3 }}>
                                Your password has been saved successfully.
                            </Alert>
                            <Typography
                                sx={{
                                    color: "text.secondary",
                                    fontFamily:
                                        "Helvetica, Arial, sans-serif",
                                    fontSize: 15,
                                    mb: 3,
                                }}
                            >
                                You can now sign in to Nodiant Online.
                            </Typography>
                            <Button
                                component={RouterLink}
                                to="/signin"
                                variant="contained"
                                fullWidth
                                sx={{
                                    minHeight: 46,
                                    fontFamily:
                                        "Helvetica, Arial, sans-serif",
                                    fontSize: 15,
                                    textTransform: "none",
                                }}
                            >
                                Continue to sign in
                            </Button>
                        </Box>
                    )}

                    {status === "error" && (
                        <Box role="alert">
                            <Alert
                                severity="error"
                                sx={{ mb: 3, textAlign: "left" }}
                            >
                                {errorMessage}
                            </Alert>
                            <Typography
                                sx={{
                                    color: "text.secondary",
                                    fontFamily:
                                        "Helvetica, Arial, sans-serif",
                                    fontSize: 15,
                                }}
                            >
                                You can request another password email from the{" "}
                                <Link component={RouterLink} to="/signin">
                                    sign-in page
                                </Link>
                                .
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </Box>
        </>
    );
}
